import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

function fmtDateFromISO(iso) {
  if (!iso) return "___________";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}

const tipPretLabels = { fix: "fixa", ora: "pe ora", zi: "pe zi", luna: "pe luna" };
const termenPlataLabels = {
  finalizare: "la finalizarea serviciilor",
  lunar: "lunar, pana la data de 15 a lunii urmatoare",
  factura15: "in termen de 15 zile de la emiterea facturii",
  factura30: "in termen de 30 zile de la emiterea facturii",
};
const instantaLabels = {
  domiciliu_prestator: "instantele judecatoresti competente de la domiciliul/sediul prestatorului",
  domiciliu_beneficiar: "instantele judecatoresti competente de la domiciliul/sediul beneficiarului",
  executare: "instantele judecatoresti competente de la locul executarii contractului",
};

function renderParty(data, prefix, isMobile) {
  const tip = data[prefix + "Tip"];
  const bold = { fontWeight: 700, color: "#2d2418" };
  const fieldRow = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "2px 0",
    paddingLeft: isMobile ? "12px" : "20px",
  };

  if (tip === "pf") {
    const ciVal = data[prefix + "CI"] ? formatCI(data[prefix + "CI"]) : "___ ___";
    return (
      <>
        <p style={fieldRow}>Nume: <span style={bold}>{data[prefix + "Nume"] || "________________________"}</span></p>
        <p style={fieldRow}>CNP: <span style={bold}>{data[prefix + "CNP"] || "________________________"}</span></p>
        <p style={fieldRow}>CI seria <span style={bold}>{ciVal}</span></p>
        <p style={fieldRow}>Domiciliul: <span style={bold}>{data[prefix + "Adresa"] || "________________________"}</span></p>
      </>
    );
  }
  if (tip === "pfa") {
    return (
      <>
        <p style={fieldRow}>Denumire: <span style={bold}>{data[prefix + "Denumire"] || "________________________"}</span></p>
        <p style={fieldRow}>CUI: <span style={bold}>{data[prefix + "CUI"] || "________________________"}</span></p>
        <p style={fieldRow}>Sediu: <span style={bold}>{data[prefix + "Sediu"] || "________________________"}</span></p>
        <p style={fieldRow}>Reprezentant: <span style={bold}>{data[prefix + "Reprezentant"] || "________________________"}</span></p>
      </>
    );
  }
  // srl
  return (
    <>
      <p style={fieldRow}>Denumire: <span style={bold}>{data[prefix + "Denumire"] || "________________________"}</span></p>
      <p style={fieldRow}>CUI: <span style={bold}>{data[prefix + "CUI"] || "________________________"}</span></p>
      {data[prefix + "RegCom"] && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data[prefix + "RegCom"]}</span></p>}
      <p style={fieldRow}>Sediu social: <span style={bold}>{data[prefix + "Sediu"] || "________________________"}</span></p>
      <p style={fieldRow}>Reprezentant legal: <span style={bold}>{data[prefix + "Reprezentant"] || "________________________"}</span></p>
    </>
  );
}

function getPartyName(data, prefix) {
  const tip = data[prefix + "Tip"];
  if (tip === "pf") return data[prefix + "Nume"] || "";
  if (tip === "pfa") return data[prefix + "Denumire"] ? `${data[prefix + "Denumire"]} prin ${data[prefix + "Reprezentant"] || ""}` : "";
  return data[prefix + "Denumire"] ? `${data[prefix + "Denumire"]} prin ${data[prefix + "Reprezentant"] || ""}` : "";
}

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };

  const pretText = `${data.pretSuma || "___"} ${data.pretMoneda || "RON"}${data.tipPret !== "fix" ? ` / ${tipPretLabels[data.tipPret] || ""}` : ""}`;

  const obligatiiPrestatorList = [
    "Sa presteze serviciile conform specificatiilor convenite in prezentul contract.",
    "Sa respecte termenele stabilite pentru executarea serviciilor.",
    "Sa informeze Beneficiarul cu privire la stadiul executarii serviciilor.",
    "Sa predea Beneficiarului rezultatele serviciilor prestate.",
    "Sa pastreze confidentialitatea informatiilor primite de la Beneficiar.",
  ];

  const obligatiiBeneficiarList = [
    "Sa puna la dispozitia Prestatorului toate informatiile si documentele necesare executarii serviciilor.",
    "Sa plateasca pretul convenit la termenele stabilite.",
    "Sa receptioneze serviciile prestate si sa comunice observatiile in termen de 5 zile lucratoare.",
    "Sa nu utilizeze rezultatele serviciilor in alt scop decat cel convenit in prezentul contract.",
  ];

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Contract de Prestari Servicii
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: 0,
        }}>
          Nr. {data.numarContract || "____"} din {today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE CONTRACTANTE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE CONTRACTANTE</h3>
        <p style={p}><span style={bold}>1.1. Prestatorul:</span></p>
        {renderParty(data, "prestator", isMobile)}

        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>

        <p style={p}><span style={bold}>1.2. Beneficiarul:</span></p>
        {renderParty(data, "beneficiar", isMobile)}

        <p style={{ ...p, fontStyle: "italic", marginTop: "16px" }}>
          au convenit incheierea prezentului contract de prestari servicii in urmatoarele conditii:
        </p>
      </div>

      {/* II. OBIECTUL CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. OBIECTUL CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Prestatorul se obliga sa presteze in favoarea Beneficiarului urmatoarele servicii:
        </p>
        <p style={indent}>
          <span style={bold}>{data.obiectContract || "________________________"}</span>
        </p>
        <p style={p}>
          <span style={bold}>2.2.</span> Serviciile vor fi prestate conform standardelor profesionale aplicabile si in conformitate cu specificatiile agreate de parti.
        </p>
      </div>

      {/* III. DURATA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. DURATA CONTRACTULUI</h3>
        {data.durataNedeterminata ? (
          <>
            <p style={p}>
              <span style={bold}>3.1.</span> Prezentul contract se incheie pe <span style={bold}>durata nedeterminata</span>, incepand cu data de <span style={bold}>{data.dataInceput ? fmtDateFromISO(data.dataInceput) : "___________"}</span>.
            </p>
            <p style={p}>
              <span style={bold}>3.2.</span> Oricare dintre parti poate denunta unilateral contractul cu un preaviz de <span style={bold}>{data.preavizReziliere || "30"} zile</span>.
            </p>
          </>
        ) : (
          <>
            <p style={p}>
              <span style={bold}>3.1.</span> Prezentul contract se incheie pe <span style={bold}>durata determinata</span>, incepand cu data de <span style={bold}>{data.dataInceput ? fmtDateFromISO(data.dataInceput) : "___________"}</span> si pana la data de <span style={bold}>{data.dataSfarsit ? fmtDateFromISO(data.dataSfarsit) : "___________"}</span>.
            </p>
            <p style={p}>
              <span style={bold}>3.2.</span> Contractul poate fi prelungit prin acordul scris al ambelor parti.
            </p>
          </>
        )}
      </div>

      {/* IV. PRETUL SI MODALITATEA DE PLATA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. PRETUL SI MODALITATEA DE PLATA</h3>
        <p style={p}>
          <span style={bold}>4.1.</span> Pretul serviciilor este de <span style={bold}>{pretText}</span>{data.tipPret === "fix" ? "" : `, suma ${tipPretLabels[data.tipPret] || ""}`}.
        </p>
        <p style={p}>
          <span style={bold}>4.2.</span> Plata se efectueaza <span style={bold}>{termenPlataLabels[data.termenPlata] || "conform intelegerii partilor"}</span>.
        </p>
        <p style={p}>
          <span style={bold}>4.3.</span> Plata se face prin transfer bancar sau numerar, cu respectarea plafonului platilor in numerar conform Legii 70/2015.
        </p>
        <p style={p}>
          <span style={bold}>4.4.</span> Intarzierea la plata atrage penalitati de 0.1% pe zi din suma datorata, incepand cu ziua urmatoare scadentei.
        </p>
      </div>

      {/* V. OBLIGATIILE PRESTATORULUI */}
      <h3 style={sectionTitle()}>V. OBLIGATIILE PRESTATORULUI</h3>
      {obligatiiPrestatorList.map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>5.{i + 1}.</span> {text}
        </p>
      ))}

      {/* VI. OBLIGATIILE BENEFICIARULUI */}
      <h3 style={sectionTitle()}>VI. OBLIGATIILE BENEFICIARULUI</h3>
      {obligatiiBeneficiarList.map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>6.{i + 1}.</span> {text}
        </p>
      ))}

      {/* VII. RECEPTIA SERVICIILOR */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VII. RECEPTIA SERVICIILOR</h3>
        <p style={indent}><span style={bold}>7.1.</span> La finalizarea serviciilor, Prestatorul va notifica Beneficiarul in vederea receptionarii.</p>
        <p style={indent}><span style={bold}>7.2.</span> Beneficiarul are la dispozitie 5 zile lucratoare pentru a verifica si receptiona serviciile prestate.</p>
        <p style={indent}><span style={bold}>7.3.</span> In cazul in care Beneficiarul nu formuleaza obiectiuni in termenul mentionat, serviciile se considera receptionate.</p>
      </div>

      {/* VIII. INCETAREA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VIII. INCETAREA CONTRACTULUI</h3>
        <p style={p}><span style={bold}>8.1.</span> Contractul inceteaza:</p>
        {[
          data.durataNedeterminata ? null : "la expirarea duratei",
          "prin acordul partilor",
          "prin reziliere pentru neexecutarea obligatiilor contractuale",
          `prin denuntare unilaterala cu preaviz de ${data.preavizReziliere || "30"} zile`,
        ].filter(Boolean).map((text, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcd"[i]})</span> {text}
          </p>
        ))}
        <p style={{ ...p, marginTop: "12px" }}>
          <span style={bold}>8.2.</span> In cazul rezilierii din culpa uneia dintre parti, partea in culpa datoreaza daune-interese celeilalte parti.
        </p>
      </div>

      {/* Dynamic sections with counter */}
      {(() => {
        const roman = ["IX","X","XI","XII","XIII","XIV","XV"];
        let s = 0;
        let a = 9;

        const sections = [];

        if (data.includeNDA) {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="nda" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. CONFIDENTIALITATE</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Partile se obliga sa pastreze confidentialitatea tuturor informatiilor, documentelor si datelor la care au acces in executarea prezentului contract.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Obligatia de confidentialitate se mentine pe toata durata contractului si pentru o perioada de 2 ani de la incetarea acestuia.</p>
              <p style={indent}><span style={bold}>{n}.3.</span> Nu constituie incalcarea obligatiei de confidentialitate dezvaluirea informatiilor la cererea autoritatilor competente, in conditiile legii.</p>
            </div>
          );
        }

        if (data.includeNonCompete) {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="noncompete" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. NON-CONCURENTA</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Pe durata contractului si pentru o perioada de 6 luni de la incetarea acestuia, Prestatorul se obliga sa nu presteze servicii identice sau similare in favoarea concurentilor directi ai Beneficiarului, fara acordul scris al acestuia.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Clauza de non-concurenta este limitata la domeniul de activitate si aria geografica in care Beneficiarul isi desfasoara activitatea.</p>
              <p style={indent}><span style={bold}>{n}.3.</span> Incalcarea obligatiei de non-concurenta da dreptul Beneficiarului la daune-interese.</p>
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="pi" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. PROPRIETATE INTELECTUALA</h3>
              {data.transferPI ? (
                <>
                  <p style={indent}><span style={bold}>{n}.1.</span> Drepturile de proprietate intelectuala asupra rezultatelor serviciilor prestate se transfera Beneficiarului la momentul platii integrale a pretului.</p>
                  <p style={indent}><span style={bold}>{n}.2.</span> Prestatorul pastreaza dreptul de a utiliza metodologiile, know-how-ul si instrumentele proprii dezvoltate anterior sau independent de prezentul contract.</p>
                </>
              ) : (
                <>
                  <p style={indent}><span style={bold}>{n}.1.</span> Drepturile de proprietate intelectuala asupra rezultatelor serviciilor prestate raman in proprietatea Prestatorului.</p>
                  <p style={indent}><span style={bold}>{n}.2.</span> Beneficiarul primeste o licenta neexclusiva de utilizare a rezultatelor, in scopul pentru care au fost create.</p>
                </>
              )}
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="raspundere" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. RASPUNDERE</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Fiecare parte raspunde pentru prejudiciile cauzate celeilalte parti prin neindeplinirea sau indeplinirea necorespunzatoare a obligatiilor asumate.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Nici una dintre parti nu raspunde pentru neexecutarea obligatiilor cauzata de forta majora, conform legii.</p>
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="litigii" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. LEGEA APLICABILA SI LITIGII</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Prezentul contract este guvernat de legea romana.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Litigiile se solutioneaza pe cale amiabila, iar in caz contrar de {instantaLabels[data.instantaCompetenta] || "instantele judecatoresti competente"}.</p>
            </div>
          );
        }

        {
          const heading = roman[s++];
          const n = a++;
          sections.push(
            <div key="finale" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{heading}. DISPOZITII FINALE</h3>
              <p style={indent}><span style={bold}>{n}.1.</span> Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti.</p>
              <p style={indent}><span style={bold}>{n}.2.</span> Prezentul contract s-a incheiat in 2 exemplare originale, cate unul pentru fiecare parte.</p>
            </div>
          );
        }

        return sections;
      })()}

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            PRESTATOR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {getPartyName(data, "prestator")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            BENEFICIAR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {getPartyName(data, "beneficiar")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ContractPreview;
